import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { NgRedux } from '@ptl/angular-redux-store';
import { selectPlanForDRX } from '../configs/reducers/plans.reducer';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { Product, MedicareAdvantage_and_PDP_Product } from '../interfaces/product.interface';
import { PlanAttributes, SummaryView, SummaryViewBenefit } from '../interfaces/plan-attributes.interface';
import { Observable, Subscription } from 'rxjs';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { planDetailsByPlanId } from '../configs/reducers/plan-details.reducer';
import { PRODUCT_GROUPS } from '../enums/product-groups.enum';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { RecommendedPlans } from '../interfaces/recommended-plans.interface';
import { PtlWindowLocationService } from '@ptl/global-components';
import { CurrencyPipe } from '@angular/common';
import { EligibilityManagerService } from '../../on-exchange-enrollment/services/eligibility-manager.service';

@Component({
    selector: 'app-plan-card',
    templateUrl: './plan-card.component.html',
    styleUrls: ['./plan-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class PlanCardComponent implements OnInit, OnDestroy {

  @Output() checkboxToggle = new EventEmitter<{ planId: string, checked: boolean }>();
  @Output() showFormTrigger = new EventEmitter<{ clicked: boolean }>();

  @ViewChild('checkbox') checkbox: MatCheckbox;
  @ViewChild('planDetailsButton', { static: true }) planDetailsButton: ElementRef;

  @Input() enrollStartUrl: string;
  @Input() specialEnrollmentUrl: string;
  @Input() planDetailsUrl: string;
  @Input() recommendedPlansUrl: string;
  @Input() addMedicationsUrl: string;
  @Input() recommendedPlanLabelOne: string;
  @Input() recommendedPlanLabelTwo: string;
  @Input() recommendedPlanLabelThree: string;
  @Input() isSep: boolean;
  @Input() showCompareCheckbox: boolean = true;

  @Input() plan: Product<PlanAttributes, SummaryView>;
  @Input() numCheckboxesChecked: Observable<number>;

  @Input() priceTooltip: string;
  @Input() starsTooltip: string;
  @Input() planCardImageUrl: string;
  @Input() planCardImageLabel: string;
  @Input() planCardImageMatchers: string;

  @Input() disableCoverageDetails: boolean;

  planCardImageRegex: RegExp;

  selectedAptc: number;
  selectedAptcSubscription: Subscription;
  monthlyPremiumWithAPTCModifier: string;

  checkboxesCheckSubscription: Subscription;
  comparableLimit = 3;
  productType: PRODUCT_GROUPS;
  productName = 'unknown_product';
  recommendedPlansFlow = false;
  recommendedPlanIds: RecommendedPlans = {
    recommendedPlan: null,
    alternate1: null,
    alternate2: null
  };

  fullStarsArray: any[] = [];
  hasHalfStar: boolean = false;

  constructor(
    private ngRedux: NgRedux<any>,
    private reducerUtilityService: ReducerUtilityService,
    private windowLocationService: PtlWindowLocationService,
    private currencyPipe: CurrencyPipe,
    private eligibilityManagerService: EligibilityManagerService,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  trackByIndex(index: number, pricingTile: SummaryViewBenefit): number {
    return index;
  }

  ngOnInit() {
    if (this.planCardImageMatchers) {
      this.planCardImageRegex = new RegExp(this.planCardImageMatchers.toLowerCase());
    }

    this.configureStarRating();

    /*
     * Subscribe to parent event which indicates current number of checked checkboxes
     * - Only disable if the box is currently unchecked
     */
    this.productName = this.plan.attributes.PRODUCT_NAME.replace(/ /g, '-');

    if (this.plan?.summaryView?.benefits) {
      this.plan.summaryView.benefits.forEach((benefit) => {
        benefit.displayValue = this.cleanAndTruncateSummaryDisplay(benefit.value);
        benefit.tooltipValue = benefit.value.replace(/\<br.*?\/\>/g, '');
      });
    }

    this.checkboxesCheckSubscription = this.numCheckboxesChecked.subscribe((num: number) => {
      if (!this.checkbox) {
        return;
      }

      if (num >= this.comparableLimit && !this.checkbox.checked) {
        this.checkbox.disabled = true;
      }
      else {
        this.checkbox.disabled = false;
      }
    });

    const plansState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    if (plansState.recommendPlansFlowUsageCode && plansState.recommendedPlanIds) {
      this.recommendedPlanIds = plansState.recommendedPlanIds;
    }

    this.selectedAptcSubscription
      = this.eligibilityManagerService
        .selectedAPTCChangedSubject.subscribe( prospectId => {
          this.selectedAptc = this.eligibilityManagerService.getSelectedMonthlyAptcForProspect(prospectId);
          this.getMonthlyPremiumWithAPTCModifier(this.plan?.price, this.plan.allowEnroll);
        });
  }

  ngOnDestroy(): void {
    if (this.checkboxesCheckSubscription) {
      this.checkboxesCheckSubscription.unsubscribe();
    }
    if (this.selectedAptcSubscription) {
      this.selectedAptcSubscription.unsubscribe();
    }
  }

  configureStarRating() {
    if (this.plan.attributes['STAR_RATING']) {
      let stars = +this.plan.attributes['STAR_RATING'];
      this.fullStarsArray = new Array(Math.floor(stars));
      this.hasHalfStar = (stars > this.fullStarsArray.length) ? true : false;
    }
  }

  getMonthlyPremium(price, allowEnroll?) {
    if (isNaN(Number(price))) {
      if (price) {
        const priceRange = price?.split('-');
        return this.currencyPipe.transform(priceRange[0]) + '-' + this.currencyPipe.transform(priceRange[1]);
      } else {
        return 'N/A';
      }
    } else {
      if (allowEnroll) {
        return this.currencyPipe.transform(Number(price));
      } else {
        return '-';
      }
    }
  }

  /** Same logic as above, but, with the modifer used */
  getMonthlyPremiumWithAPTCModifier(price, allowEnroll?) {
    this.monthlyPremiumWithAPTCModifier = (() => {
      if (isNaN(Number(price))) {
        if (price) {
          const priceRange = price?.split('-');
          let aptcCreditOne = priceRange[0] - this.selectedAptc;
          if (aptcCreditOne < 0) {
            aptcCreditOne = 0;
          }
          let aptcCreditTwo = priceRange[1] - this.selectedAptc;
          if (aptcCreditTwo < 0) {
            aptcCreditTwo = 0;
          }
          return (
            this.currencyPipe.transform(aptcCreditOne) +
            '-' +
            this.currencyPipe.transform(aptcCreditTwo)
          );
        } else {
          return 'N/A';
        }
      } else {
        if (allowEnroll) {
          const difference = Number(price) - this.selectedAptc;
          if (difference < 0) {
            return this.currencyPipe.transform(0);
          } else {
            return this.currencyPipe.transform(difference);
          }
        } else {
          return '-';
        }
      }
    })();
    this.changeDetectorRef.detectChanges();
  }

  goToPlanDetails(): void {
    this.selectPlan();
    const finalUrl = `${this.planDetailsUrl}?planName=${this.plan.attributes.PRODUCT_NAME}`;
    this.windowLocationService.goToUrl(finalUrl);
  }

  selectPlan() {
    /** @TODO this needs to be Medicare Advantage to PDP plan ONLY */
    this.ngRedux.dispatch(selectPlanForDRX(this.plan as MedicareAdvantage_and_PDP_Product));
    this.ngRedux.dispatch(planDetailsByPlanId(this.plan.attributes.PLAN_ID));
  }

  emitChecked(event: MatCheckboxChange, planId: string) {
    this.checkboxToggle.emit({ planId, checked: event.checked });
  }

  emitShowForm() {
    this.showFormTrigger.emit({ clicked: true });
  }

  hidePlanDetailsInteraction() {
    // TODO: PLANSUMMARY: Get this from CONFIG OR from ContentStorage Update this to be configurable from ProductTabsConfig
    return this.plan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE.toUpperCase();
  }

  hideComparePlansCheckbox() {
    return !this.showCompareCheckbox || this.plan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE.toUpperCase();
  }

  cleanAndTruncateSummaryDisplay(valueString: string) {
    if (valueString) {
      // Special case for handling N/A, Yes, and No
      if (this.isRegularTextValue(valueString)) {
        return valueString;
      }

      const alphaRegExp = new RegExp('[a-zA-Z]');
      const dollarRegExp = new RegExp('[0-9$]');

      if (dollarRegExp.test(valueString[0])) {
        // ex: '30% after coinsurance' (first character is a number)
        const splitString = valueString.split(' ');
        if (splitString && splitString[0]) {
          return splitString[0];
        }
      } else if (alphaRegExp.test(valueString[0])) {
        // ex: 'institutional $500 / professional $1000
        return valueString.replace(/[a-zA-Z ]/g, '');
      }
    }
    else {
      return 'N/A';
    }

    return valueString;
  }

  isRegularTextValue(valueString: string) {
    let testString = valueString.toLowerCase().trim();

    if (testString === 'n/a' || testString === 'yes' || testString === 'no') {
      return true;
    }

    return false;
  }

  public canDisableEnroll() {
    const allowEnrollDisabled = !this.plan?.allowEnroll;
    const noPlanIdFound = !this.plan?.attributes?.PLAN_ID;
    const noValidUrl = (!this.enrollStartUrl && !this.plan?.attributes?.VENDOR_URL);
    return !(allowEnrollDisabled || noPlanIdFound || noValidUrl);
  }
}
